<template>
	<div class="chat-list-container">
		<Loading v-if="isLoading" />
		<template v-else>
			<!-- Header -->
			<div class="page-header">
				<h1 class="page-title">{{ $t('listChat.txtTitle') || 'Mensajes' }}</h1>
				<p class="page-subtitle" v-if="items.length > 0">
					{{ items.length }} {{ $t('listChat.txtConversations') || 'conversaciones' }}
				</p>
			</div>

			<!-- Chat List -->
			<div class="chat-list" v-if="items.length > 0">
				<div
					v-for="item in items"
					:key="item.id"
					class="chat-item"
					:class="{ 'has-unread': item.haveMessages }"
					@click="redirectChat(item.id)"
				>
					<div class="chat-avatar">
						<v-icon size="24" color="white">mdi-account</v-icon>
						<div v-if="item.haveMessages" class="unread-badge"></div>
					</div>

					<div class="chat-content">
						<div class="chat-header">
							<span class="chat-name">{{ getClientName(item) }}</span>
							<span class="chat-time">{{ formatDate(item.action) }}</span>
						</div>
						<div class="chat-preview">
							<span class="service-number">#{{ item.serviceNumber }}</span>
							<span class="chat-message">{{ item.subtitle }}</span>
						</div>
					</div>

					<div class="chat-indicator">
						<v-icon
							size="20"
							:color="item.haveMessages ? '#ffc107' : '#ddd'"
						>
							{{ item.haveMessages ? 'mdi-message' : 'mdi-message-outline' }}
						</v-icon>
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state">
				<div class="empty-icon">
					<v-icon size="48" color="#ccc">mdi-message-outline</v-icon>
				</div>
				<p class="empty-text">{{ $t('listChat.noMessages') }}</p>
			</div>
		</template>
	</div>
</template>

<script>
import { mapActions, mapState } from "vuex";

const Loading = () =>
	import(/* webpackChunkName: "Loading" */ "@/components/loading.vue");

export default {
	name: "ListChat",
	components: { Loading },
	data: () => ({
		isLoading: true,
	}),
	methods: {
		formatDate(date) {
			return this.$moment(date.toDate())
				.locale("es")
				.fromNow();
		},
		...mapActions("ui", {
			disabledNavigation: "disabledNavigation",
			activeBackBtn: "activeBackBtn",
		}),
		redirectChat(id) {
			this.$router.push({ params: { id }, name: "Chat" });
		},
		getClientName(item) {
			try {
				return item.client.firstName || item.id;
			} catch (err) {
				return item.id;
			}
		},
	},
	computed: {
		...mapState("chat", {
			items: (state) => state.activeChats,
		}),
	},
	mounted() {
		setTimeout(() => {
			this.disabledNavigation();
			this.activeBackBtn();
			this.isLoading = false;
		}, 250);
	},
};
</script>

<style scoped>
.chat-list-container {
	min-height: 100vh;
	background: #fafafa;
}

.page-header {
	background: #fff;
	padding: 20px 16px 16px;
	border-bottom: 1px solid #eee;
}

.page-title {
	font-size: 20px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0 0 4px;
}

.page-subtitle {
	font-size: 13px;
	color: #888;
	margin: 0;
}

.chat-list {
	padding: 8px 12px;
}

.chat-item {
	display: flex;
	align-items: center;
	gap: 12px;
	background: #fff;
	border-radius: 12px;
	padding: 14px;
	margin-bottom: 8px;
	cursor: pointer;
	transition: all 0.15s ease;
	border: 1px solid transparent;
}

.chat-item:active {
	transform: scale(0.98);
	background: #f8f8f8;
}

.has-unread {
	border-color: #6200ea;
	background: #faf8ff;
}

.chat-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	position: relative;
}

.unread-badge {
	position: absolute;
	top: 0;
	right: 0;
	width: 14px;
	height: 14px;
	background: #ffc107;
	border-radius: 50%;
	border: 2px solid #fff;
}

.chat-content {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.chat-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
}

.chat-name {
	font-size: 15px;
	font-weight: 600;
	color: #1a1a1a;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.chat-time {
	font-size: 11px;
	color: #999;
	flex-shrink: 0;
}

.chat-preview {
	display: flex;
	align-items: center;
	gap: 6px;
}

.service-number {
	font-size: 11px;
	font-weight: 600;
	color: #6200ea;
	background: #ede7f6;
	padding: 2px 6px;
	border-radius: 4px;
	flex-shrink: 0;
}

.chat-message {
	font-size: 13px;
	color: #666;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.chat-indicator {
	flex-shrink: 0;
	padding-left: 4px;
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 80px 20px;
	text-align: center;
}

.empty-icon {
	width: 80px;
	height: 80px;
	border-radius: 50%;
	background: #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;
}

.empty-text {
	font-size: 14px;
	color: #999;
	margin: 0;
}
</style>
